import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  SelectChangeEvent
} from '@mui/material';
import {
  Search as SearchIcon,
  Sort as SortIcon,
  ArrowUpward as AscIcon,
  ArrowDownward as DescIcon,
} from '@mui/icons-material';
import DriverCard from './DriverCard';
import { Driver } from '../../models/userInfo';

interface DriverTableProps {
  drivers: Driver[];
  selectedDrivers: string[];
  onDriverSelect: (vehicleId: string) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  sortConfig: {
    key: 'name' | 'wtCapKg' | 'maxTp' | 'maxRangeKm' | 'vehicleType';
    direction: 'asc' | 'desc';
  };
  onSortChange: (key: 'name' | 'wtCapKg' | 'maxTp' | 'maxRangeKm' | 'vehicleType', direction: 'asc' | 'desc') => void;
}

/**
 * Component to display a searchable, sortable table of drivers
 */
const DriverTable: React.FC<DriverTableProps> = ({
  drivers,
  selectedDrivers,
  onDriverSelect,
  searchTerm,
  onSearchChange,
  sortConfig,
  onSortChange
}) => {
  // State for filtered drivers
  const [filteredDrivers, setFilteredDrivers] = useState<Driver[]>(drivers);
  
  // Filter and sort drivers when any dependency changes
  useEffect(() => {
    let result = [...drivers];
    
    // Apply search filter
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(driver => 
        driver.name.toLowerCase().includes(lowerTerm) ||
        driver.phNo.includes(searchTerm) ||
        driver.vendorName.toLowerCase().includes(lowerTerm) ||
        driver.vehicle.regNumber.toLowerCase().includes(lowerTerm) ||
        driver.vehicle.vehicleType.toLowerCase().includes(lowerTerm)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aValue, bValue;
      
      // Extract values based on sort key
      switch (sortConfig.key) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'wtCapKg':
          aValue = a.vehicle.wtCapKg;
          bValue = b.vehicle.wtCapKg;
          break;
        case 'maxTp':
          aValue = a.vehicle.maxTp;
          bValue = b.vehicle.maxTp;
          break;
        case 'maxRangeKm':
          aValue = a.vehicle.maxRangeKm;
          bValue = b.vehicle.maxRangeKm;
          break;
        case 'vehicleType':
          aValue = a.vehicle.vehicleType;
          bValue = b.vehicle.vehicleType;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }
      
      // Compare values
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredDrivers(result);
  }, [drivers, searchTerm, sortConfig]);
  
  // Handle sort option selection
  const handleSortChange = (event: SelectChangeEvent) => {
    onSortChange(
      event.target.value as 'name' | 'wtCapKg' | 'maxTp' | 'maxRangeKm' | 'vehicleType',
      sortConfig.direction
    );
  };
  
  // Toggle sort direction
  const toggleSortDirection = () => {
    onSortChange(
      sortConfig.key,
      sortConfig.direction === 'asc' ? 'desc' : 'asc'
    );
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Search and filter controls */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        sx={{ mb: 3 }}
        alignItems="center"
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search drivers..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 200 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select
              labelId="sort-by-label"
              value={sortConfig.key}
              label="Sort By"
              onChange={handleSortChange}
              startAdornment={
                <InputAdornment position="start">
                  <SortIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="name">Driver Name</MenuItem>
              <MenuItem value="wtCapKg">Weight Capacity</MenuItem>
              <MenuItem value="maxTp">Max Touchpoints</MenuItem>
              <MenuItem value="maxRangeKm">Max Range</MenuItem>
              <MenuItem value="vehicleType">Vehicle Type</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title={`Sort ${sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'}`}>
            <IconButton onClick={toggleSortDirection} size="small">
              {sortConfig.direction === 'asc' ? <AscIcon /> : <DescIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
      
      <Divider sx={{ mb: 2 }} />
      
      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredDrivers.length} of {drivers.length} drivers
      </Typography>
      
      {/* Driver cards list */}
      <Box>
        {filteredDrivers.length > 0 ? (
          filteredDrivers.map(driver => (
            <DriverCard
              key={driver.vehicle.vehicleId}
              driver={driver}
              isSelected={selectedDrivers.includes(driver.vehicle.vehicleId)}
              onClick={() => onDriverSelect(driver.vehicle.vehicleId)}
            />
          ))
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
            No drivers match your search criteria
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DriverTable; 
